import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Wallet as WalletIcon, TrendingUp, Award, Gift, Shield, AlertCircle, CheckCircle, Clock, ArrowUpRight, History, Bell, TrendingDown } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../i18n/LanguageContext';
import { walletApi, cryptoApi } from '../utils/api';
import api from '../utils/api';
import Header from '../components/Header';
import PriceChart from '../components/PriceChart';
import CurrencyIcon from '../components/CurrencyIcon';
import { FadeUp, StaggerContainer, StaggerItem } from '../components/Motion';

const Dashboard = () => {
  const { user } = useAuth();
  const { t } = useLanguage();
  const [wallet, setWallet] = useState(null);
  const [rates, setRates] = useState({});
  const [totalValue, setTotalValue] = useState(0);
  const [cashbackInfo, setCashbackInfo] = useState(null);
  const [kycStatus, setKycStatus] = useState(null);
  const [exchangeHistory, setExchangeHistory] = useState([]);
  const [priceAlerts, setPriceAlerts] = useState([]);
  const [selectedCrypto, setSelectedCrypto] = useState('BTC');

  useEffect(() => { fetchData(); }, []);
  useEffect(() => { calculateTotalValue(); }, [wallet, rates]);

  const fetchData = async () => {
    try { const r = await cryptoApi.getRates(); setRates(r.data); } catch {}
    try { const r = await walletApi.getWallet(); setWallet(r.data); } catch {}
    try { const r = await api.get('/user/cashback'); setCashbackInfo(r.data); } catch {}
    try { const r = await api.get('/kyc/status'); setKycStatus(r.data); } catch { setKycStatus({ status: 'not_started' }); }
    try { const r = await api.get('/user/exchanges'); setExchangeHistory(r.data?.exchanges || []); } catch {}
    try { const r = await api.get('/alerts'); setPriceAlerts((r.data?.alerts || []).filter(a => a.status === 'active')); } catch {}
  };

  const calculateTotalValue = () => {
    if (!wallet || !rates) return;
    let total = 0;
    Object.entries(wallet.balances || {}).forEach(([symbol, balance]) => {
      const price = rates[symbol]?.price || rates[symbol]?.usd || 0;
      total += balance * price;
    });
    setTotalValue(total);
  };

  const getKycStatusBadge = () => {
    const cfg = {
      not_started: { icon: AlertCircle, text: t.dashboard?.notVerified || 'Non vérifié', cls: 'bg-white/10 text-white/60' },
      pending: { icon: Clock, text: t.dashboard?.pending || 'En cours', cls: 'bg-amber-400/20 text-amber-300' },
      approved: { icon: CheckCircle, text: t.dashboard?.verified || 'Vérifié', cls: 'bg-emerald-400/20 text-emerald-300' },
      rejected: { icon: AlertCircle, text: t.dashboard?.rejected || 'Rejeté', cls: 'bg-rose-400/20 text-rose-300' },
    };
    const status = kycStatus?.status || 'not_started';
    const c = cfg[status];
    const Icon = c.icon;
    return (
      <div className={`inline-flex items-center space-x-1.5 px-3 py-1.5 rounded-full ${c.cls}`} data-testid="kyc-status-badge">
        <Icon className="w-4 h-4" />
        <span className="text-sm font-semibold">{c.text}</span>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-[#F5F6F7]" data-testid="patient-dashboard">
      <Header />

      {/* Black hero band — Revolut app style */}
      <div className="bg-black px-6 pt-10 pb-24">
        <div className="max-w-7xl mx-auto">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
            className="flex flex-col md:flex-row md:items-end justify-between gap-6">
            <div>
              <p className="text-white/50 text-sm mb-2">{t.dashboard?.welcome || 'Bonjour'}, {user?.name || user?.email?.split('@')[0]}</p>
              <div className="text-white font-extrabold text-5xl md:text-6xl tracking-tight font-mono-data" data-testid="total-value" style={{ fontFamily: 'Manrope' }}>
                ${totalValue.toLocaleString('fr-FR', { maximumFractionDigits: 2 })}
              </div>
              <p className="text-white/40 text-sm mt-2">{t.dashboard?.totalValue || 'Valeur totale du portefeuille'}</p>
            </div>
            <div className="flex items-center space-x-3">
              {getKycStatusBadge()}
              {kycStatus?.status !== 'approved' && kycStatus?.status !== 'pending' && (
                <Link to="/kyc" data-testid="kyc-verify-btn">
                  <button className="btn-pill-white flex items-center space-x-2 px-5 py-2.5 text-sm">
                    <Shield className="w-4 h-4" /><span>{t.dashboard?.verifyIdentity || 'Vérifier mon identité'}</span>
                  </button>
                </Link>
              )}
              <Link to="/exchange" data-testid="dashboard-exchange-btn">
                <button className="btn-pill-ghost-dark flex items-center space-x-2 px-5 py-2.5 text-sm">
                  <span>{t.dashboard?.exchangeNow || 'Échanger'}</span><ArrowUpRight className="w-4 h-4" />
                </button>
              </Link>
            </div>
          </motion.div>
        </div>
      </div>

      <div className="px-6 pb-16 -mt-14" data-testid="dashboard-container">
        <div className="max-w-7xl mx-auto">
          {/* Stat cards */}
          <StaggerContainer className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            {[
              { icon: WalletIcon, label: t.dashboard?.totalValue || 'Valeur totale', value: `$${totalValue.toFixed(2)}`, testId: 'total-balance-card' },
              { icon: TrendingUp, label: t.dashboard?.totalVolume || 'Volume total', value: `$${cashbackInfo?.total_volume?.toFixed(2) || '0.00'}` },
              { icon: Award, label: t.dashboard?.vipLevel || 'Niveau VIP', value: `${t.common?.level || 'Niveau'} ${cashbackInfo?.user_level || 1}` },
              { icon: Gift, label: t.dashboard?.cashbackEarned || 'Cashback gagné', value: `$${cashbackInfo?.cashback_earned?.toFixed(2) || '0.00'}` },
            ].map((card, idx) => {
              const Icon = card.icon;
              return (
                <StaggerItem key={idx} className="bg-white rounded-3xl p-6 shadow-[0_8px_30px_rgb(0,0,0,0.05)]" data-testid={card.testId || `stat-card-${idx}`}>
                  <div className="w-11 h-11 rounded-full bg-gray-100 flex items-center justify-center mb-4">
                    <Icon className="w-5 h-5 text-gray-900" strokeWidth={1.8} />
                  </div>
                  <div className="text-sm text-gray-400 mb-1">{card.label}</div>
                  <div className="text-2xl font-extrabold text-gray-900 font-mono-data tracking-tight">{card.value}</div>
                </StaggerItem>
              );
            })}
          </StaggerContainer>

          {kycStatus?.status === 'not_started' && (
            <FadeUp className="gradient-pink-violet rounded-3xl p-6 mb-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 text-white">
              <div className="flex items-center space-x-3">
                <Shield className="w-6 h-6" />
                <div>
                  <p className="font-bold">{t.common?.kycRequired || "Vérification d'identité requise"}</p>
                  <p className="text-sm text-white/80">{t.common?.kycRequiredDesc || "Complétez votre KYC pour augmenter vos limites d'échange"}</p>
                </div>
              </div>
              <Link to="/kyc" data-testid="kyc-banner-btn">
                <button className="btn-pill-white px-6 py-2.5 text-sm">{t.common?.start || 'Commencer'}</button>
              </Link>
            </FadeUp>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2">
              <div className="mb-4 flex items-center space-x-2">
                {['BTC', 'ETH', 'SOL', 'BNB'].map((crypto) => (
                  <button key={crypto} onClick={() => setSelectedCrypto(crypto)}
                    data-testid={`chart-select-${crypto}`}
                    className={`flex items-center space-x-2 px-4 py-2 rounded-full text-sm font-semibold transition-colors ${
                      selectedCrypto === crypto ? 'bg-[#191C1F] text-white' : 'bg-white text-gray-500 hover:text-gray-900'
                    }`}>
                    <CurrencyIcon symbol={crypto} size={16} />
                    <span>{crypto}</span>
                  </button>
                ))}
              </div>
              <PriceChart symbol={selectedCrypto} currentPrice={rates[selectedCrypto]?.usd || rates[selectedCrypto]?.price || 0} change24h={rates[selectedCrypto]?.change_24h || 0} />
            </div>

            <div className="space-y-6">
              <FadeUp className="bg-white rounded-3xl p-6 shadow-[0_8px_30px_rgb(0,0,0,0.05)]" data-testid="wallet-balances">
                <h3 className="text-lg font-extrabold text-gray-900 mb-4 tracking-tight" style={{ fontFamily: 'Manrope' }}>{t.dashboard?.wallet || 'Portefeuille'}</h3>
                <div className="space-y-2">
                  {wallet && Object.entries(wallet.balances || {}).slice(0, 5).map(([symbol, balance]) => (
                    <div key={symbol} className="flex items-center justify-between p-3 rounded-2xl hover:bg-gray-50 transition-colors" data-testid={`balance-${symbol}`}>
                      <div className="flex items-center space-x-3">
                        <CurrencyIcon symbol={symbol} size={36} />
                        <div>
                          <div className="font-bold text-gray-900 text-sm">{symbol}</div>
                          <div className="text-xs text-gray-400 font-mono-data">{balance.toFixed(4)}</div>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="font-bold text-gray-900 text-sm font-mono-data">${(balance * (rates[symbol]?.price || rates[symbol]?.usd || 0)).toFixed(2)}</div>
                        <div className={`text-xs font-mono-data ${(rates[symbol]?.change_24h || 0) >= 0 ? 'text-emerald-500' : 'text-rose-500'}`}>
                          {(rates[symbol]?.change_24h || 0) >= 0 ? '+' : ''}{(rates[symbol]?.change_24h || 0).toFixed(2)}%
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </FadeUp>

              <FadeUp delay={0.05} className="bg-white rounded-3xl p-6 shadow-[0_8px_30px_rgb(0,0,0,0.05)]" data-testid="price-alerts-widget">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-extrabold text-gray-900 tracking-tight flex items-center space-x-2" style={{ fontFamily: 'Manrope' }}>
                    <Bell className="w-5 h-5 text-gray-400" /><span>{t.alerts?.title || 'Alertes de prix'}</span>
                  </h3>
                  <Link to="/alerts" data-testid="manage-alerts-link" className="text-sm font-semibold text-gray-900 bg-gray-100 px-3 py-1 rounded-full hover:bg-gray-200 transition-colors">
                    {t.alerts?.manage || 'Gérer'}
                  </Link>
                </div>
                {priceAlerts.length === 0 ? (
                  <p className="text-sm text-gray-500">{t.alerts?.noActiveShort || 'Aucune alerte active.'}{' '}
                    <Link to="/alerts" data-testid="create-alert-link" className="font-semibold text-gray-900 underline">{t.alerts?.createOne || 'Créer une alerte'}</Link>
                  </p>
                ) : (
                  <div className="space-y-2">
                    {priceAlerts.slice(0, 3).map((al) => (
                      <div key={al.id} className="flex items-center justify-between p-3 rounded-2xl bg-gray-50" data-testid={`dashboard-alert-${al.id}`}>
                        <div className="flex items-center space-x-3">
                          <CurrencyIcon symbol={al.symbol} size={28} />
                          <span className="font-bold text-gray-900 text-sm">{al.symbol}</span>
                        </div>
                        <div className={`text-xs font-semibold flex items-center space-x-1 ${al.direction === 'above' ? 'text-emerald-600' : 'text-rose-600'}`}>
                          {al.direction === 'above' ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
                          <span className="font-mono-data">{al.target_price.toLocaleString('fr-FR')} {al.currency === 'eur' ? '€' : '$'}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </FadeUp>

              <FadeUp delay={0.1} className="bg-white rounded-3xl p-6 shadow-[0_8px_30px_rgb(0,0,0,0.05)]">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-extrabold text-gray-900 tracking-tight" style={{ fontFamily: 'Manrope' }}>{t.dashboard?.cashback || 'Cashback'}</h3>
                  <span className="text-sm text-gray-900 font-semibold bg-gray-100 px-3 py-1 rounded-full">{t.common?.level || 'Niveau'} {cashbackInfo?.user_level || 1}</span>
                </div>
                <div className="mb-4">
                  <div className="flex justify-between text-xs mb-1.5">
                    <span className="text-gray-400">{t.dashboard?.progression || 'Progression'}</span>
                    <span className="font-semibold text-gray-900 font-mono-data">{(cashbackInfo?.progress_to_next || 0).toFixed(0)}%</span>
                  </div>
                  <div className="w-full bg-gray-100 rounded-full h-2">
                    <div className="bg-[#191C1F] rounded-full h-2 transition-all duration-500" style={{ width: `${Math.min(cashbackInfo?.progress_to_next || 0, 100)}%` }}></div>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="bg-gray-50 rounded-2xl p-4 text-center">
                    <div className="text-lg font-extrabold text-gray-900 font-mono-data">${cashbackInfo?.cashback_earned?.toFixed(2) || '0.00'}</div>
                    <div className="text-xs text-gray-400">{t.dashboard?.earned || 'Gagné'}</div>
                  </div>
                  <div className="gradient-blue rounded-2xl p-4 text-center text-white">
                    <div className="text-lg font-extrabold font-mono-data">{cashbackInfo?.cashback_rate_percent || '0.1%'}</div>
                    <div className="text-xs text-white/80">{t.dashboard?.currentRate || 'Taux actuel'}</div>
                  </div>
                </div>
              </FadeUp>
            </div>
          </div>

          {exchangeHistory.length > 0 && (
            <FadeUp className="mt-8 bg-white rounded-3xl p-6 shadow-[0_8px_30px_rgb(0,0,0,0.05)]" data-testid="exchange-history">
              <h3 className="text-lg font-extrabold text-gray-900 flex items-center space-x-2 mb-4 tracking-tight" style={{ fontFamily: 'Manrope' }}>
                <History className="w-5 h-5 text-gray-400" /><span>{t.dashboard?.exchangeHistory || 'Historique des échanges'}</span>
              </h3>
              <div className="space-y-2">
                {exchangeHistory.slice(0, 5).map((exchange, idx) => (
                  <div key={idx} className="flex items-center justify-between p-4 bg-gray-50 rounded-2xl">
                    <div className="flex items-center space-x-3">
                      <CurrencyIcon symbol={exchange.give_method?.symbol || 'BTC'} size={32} />
                      <div>
                        <div className="font-semibold text-gray-900 text-sm font-mono-data">
                          {exchange.give_amount} {exchange.give_method?.symbol || exchange.give_method?.name || 'N/A'} → {exchange.receive_amount} {exchange.receive_method?.symbol || exchange.receive_method?.name || 'N/A'}
                        </div>
                        <div className="text-xs text-gray-400">{new Date(exchange.created_at).toLocaleDateString('fr-FR')}</div>
                      </div>
                    </div>
                    <span className={`text-xs font-bold px-3 py-1.5 rounded-full ${
                      exchange.status === 'completed' ? 'bg-emerald-100 text-emerald-700' : exchange.status?.includes('pending') || exchange.status === 'payment_confirming' ? 'bg-amber-100 text-amber-700' : 'bg-gray-200 text-gray-500'
                    }`}>
                      {exchange.status}
                    </span>
                  </div>
                ))}
              </div>
            </FadeUp>
          )}
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
